/**
 * NavTabs — fixed bottom navigation bar for the three main Calestea tabs.
 *
 * Tabs: Home (/home) · Course (/course) · Me (/me)
 * Active tab receives gold gradient text and filled glyph.
 * aria-current="page" marks the active tab for assistive technology.
 * Each tap target is ≥ 44px in height per WCAG 2.5.5.
 * Safe-area-inset-bottom is applied so the bar clears the iOS home indicator.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './NavTabs.module.css';

// --------------------------------------------------------------------------
// Glyphs — simple inline SVG icons, consistent stroke weight
// --------------------------------------------------------------------------

function HomeGlyph({ filled }: { filled: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.glyph}
    >
      {filled ? (
        <path
          d="M10 2L2 8.5V18H7.5V13H12.5V18H18V8.5L10 2Z"
          fill="currentColor"
        />
      ) : (
        <path
          d="M10 2L2 8.5V18H7.5V13H12.5V18H18V8.5L10 2Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          fill="none"
        />
      )}
    </svg>
  );
}

function CourseGlyph({ filled }: { filled: boolean }): JSX.Element {
  // A closed book with a four-pointed star on its cover.
  const cover = 'M4 3.5C4 2.67 4.67 2 5.5 2H16V15H5.5C4.67 15 4 15.67 4 16.5V3.5Z';
  const star = 'M10 4L10.95 6.8L13.75 7.75L10.95 8.7L10 11.5L9.05 8.7L6.25 7.75L9.05 6.8L10 4Z';
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.glyph}
    >
      {filled ? (
        <path d={`${cover}${star}`} fill="currentColor" fillRule="evenodd" />
      ) : (
        <>
          <path d={cover} stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d={star} fill="currentColor" />
        </>
      )}
      <path
        d="M4 16.5C4 17.33 4.67 18 5.5 18H16V15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MeGlyph({ filled }: { filled: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.glyph}
    >
      {filled ? (
        <>
          <circle cx="10" cy="6.5" r="3.5" fill="currentColor" />
          <path
            d="M3 17.5C3 14.186 6.134 11.5 10 11.5C13.866 11.5 17 14.186 17 17.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
        </>
      ) : (
        <>
          <circle cx="10" cy="6.5" r="3.5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M3 17.5C3 14.186 6.134 11.5 10 11.5C13.866 11.5 17 14.186 17 17.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </>
      )}
    </svg>
  );
}

// --------------------------------------------------------------------------
// Tab definition
// --------------------------------------------------------------------------

interface TabDef {
  to: string;
  labelKey: string;
  Glyph: React.ComponentType<{ filled: boolean }>;
}

const TABS: TabDef[] = [
  { to: '/home',    labelKey: 'nav.home',    Glyph: HomeGlyph    },
  { to: '/course',  labelKey: 'nav.course',  Glyph: CourseGlyph  },
  { to: '/me',      labelKey: 'nav.me',      Glyph: MeGlyph      },
];

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export function NavTabs(): JSX.Element {
  const location = useLocation();
  const { t } = useTranslation('home');

  return (
    <nav
      aria-label={t('nav.mainAria')}
      className={styles.nav}
    >
      {TABS.map(({ to, labelKey, Glyph }) => {
        const isActive = location.pathname === to;
        const label = t(labelKey);
        return (
          <NavLink
            key={to}
            to={to}
            className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
            aria-current={isActive ? 'page' : undefined}
            data-testid={`nav-tab-${labelKey.split('.')[1]}`}
          >
            <Glyph filled={isActive} />
            <span className={styles.label}>{label.toUpperCase()}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
